/* eslint-disable max-lines */
import React, {useCallback, useMemo, useState} from "react";
import {useFormState} from "../../hooks/useFormState";
import {useTranslation} from "react-i18next";
import {gql, useLazyQuery} from "@apollo/client";
import cn from "classnames";
import {
  Project,
  ProjectParticipationState,
  Group,
  Challenge,
  ChallengeProject,
  ChallengeParticipantsType,
  ChallengeParticipationState,
  SortDirection,
  User,
} from "../../schema";

import Button from "../../ui/button";
import Select from "../../ui/select";
import UsersSelect, {Props as UsersSelectProps} from "./UsersSelect";

import classes from "./AssignModal.module.css";
import DatePicker from "../../ui/datepicker";
import {dateToMoscowTimestamp, getCurrentMoscowTime} from "../../time";
import WithTooltip from "../../ui/tooltip";
import {HelpCircleIcon} from "../../ui/icons";
import {AccountGroupSortField} from "../../scenes/account/groups";
import {SortInput} from "../../ui/sortbutton";
import {sortGroups} from "../../scenes/account/groups.utils";
import {challengesErrorsQuery, QueryData as ChallengeQueryData} from "./members-table/MemberEditDropdown.graphql";
import MultiSelectField, {ChallengeBase, ChallengeOption, ChallengeParticipants} from "../../ui/MultiSelectField";
import Checkbox from "../../ui/checkbox";
import Link from "../../ui/link";
import {MultiValue} from "react-select";
import AdminText from "./AdminText";

export type PickedChallenge = Pick<Challenge, "id" | "name"> & {
  __typename?: "Challenge";
};

type PickedProject = Pick<Project, "id" | "name"> & {
  __typename?: "Project";
  challenges: Array<Pick<Challenge, "id" | "name">>;
};

export type PickedChallengeProject = PickedChallenge | PickedProject;

export type QueryData = {
  challenges?: PickedChallenge[];
  projects?: PickedProject[];
  projectsWithErrors?: ProjectParticipationState[];
  challengesWithErrors?: ChallengeParticipationState[];
  user?: string;
  isSingleAssignment?: boolean;
};

export type ContentProps = React.ComponentProps<"div"> &
  (
    | {
        initialId?: Challenge["id"];
        initialType?: undefined;
      }
    | {
        initialId: ChallengeProject["id"];
        initialType: ChallengeProject["__typename"];
      }
  ) &
  QueryData & {
    params:
      | {
          multipleUsers?: false;
          onAssign?: InviteParticipants;
        }
        | ({
          multipleUsers: true;
          onAssign?: InviteParticipants;
        } & Pick<UsersSelectProps, "users" | "groups">);
  };

  export type InviteParticipants = (
    challenges?: ChallengeParticipantsType[],
    usersIds?: User["id"][],
    emails?: string[],
    adminText?: string
  ) => void;

export function AssignModalHeader() {
  const {t} = useTranslation();

  return <span>{t("components.AssignModal.assignModalTitle")}</span>;
}

export function AssignModalContent({
  initialId,
  initialType,
  challenges,
  projects,
  user,
  isSingleAssignment,
  params
}: ContentProps) {
  const {t} = useTranslation();

  const [selectedOptions, setSelectedOptions] = useState<ChallengeOption[]>([]);

  const [query, {data, loading: fetching}] = useLazyQuery<ChallengeQueryData, {userId: string | undefined}>(
    challengesErrorsQuery,
    {
      notifyOnNetworkStatusChange: true,
    },
  );

  React.useEffect(() => {
    if (isSingleAssignment) {
      const variables = {
        userId: user,
      };

      query({variables});
    }
  }, [query, isSingleAssignment, user]);

  const {challenges: challengesWithErrors, projects: projectsWithErrors} = data?.root ?? {};

  const challengeIdsInsideProjects = useMemo(() => {
    const ids = new Set<string>();
    for (const p of projects ?? []) {
      for (const c of p.challenges) {
        ids.add(c.id);
      }
    }
    for (const p of projectsWithErrors ?? []) {
      for (const c of p.challenges) {
        ids.add(c.id);
      }
    }
    return ids;
  }, [projects, projectsWithErrors]);

  const {multipleUsers} = params;
  const nowDate = new Date();
  const [activateAt, setActivateAt] = useState<Date | undefined>(undefined);
  const [deactivateAt, setDeactivateAt] = useState<Date | undefined>(undefined);
  const [adminText, setAdminText] = useState<string>("");

  const formState = useFormState<{
    id?: string | "no";
    type: NonNullable<ChallengeProject["__typename"]>;
  }>({
    initialValues: {
      id: initialId,
      type: initialType ?? "Challenge",
    },

    preventDefault: true,
    onSubmit: (data) => {
      if (!data.id && params.multipleUsers) {
        return;
      }

      const challenges = selectedOptions
        .filter((option) => !option.alreadyAssigned)
        .map((option) => ({
          challengeId: option.value,
          activateAt: option.activateAt,
          deactivateAt: option.deactivateAt,
        }));

      params.onAssign && params.onAssign(challenges as ChallengeParticipantsType[], [], [], adminText);
    },
  });

  const {type, id: currentId} = formState.values;

  React.useEffect(() => {
    if (formState.values.id && type === "Project") {
      let selectedProjects;
      if (multipleUsers) {
        selectedProjects =
          projects
            ?.find((project) => project.id === formState.values.id)
            ?.challenges.map((challenge) => ({
              value: challenge.id,
              label: challenge.name,
              alreadyAssigned: undefined,
              activateAt: undefined,
              deactivateAt: undefined,
            })) || [];
      } else {
        selectedProjects =
          projectsWithErrors
            ?.find((project) => project.id === formState.values.id)
            ?.challenges.map((challenge) => ({
              value: challenge.id,
              label: challenge.name,
              alreadyAssigned: challenge.alreadyAssigned ? challenge.alreadyAssigned : undefined,
              activateAt: challenge.activateAt,
              deactivateAt: challenge.deactivateAt,
            })) || [];
      }
      setSelectedOptions(selectedProjects);
      setCheckedOptions([]);
      setActivateAt(undefined);
      setDeactivateAt(undefined);
    } else {
      setSelectedOptions([]);
      setCheckedOptions([]);
    }
  }, [formState.values.id, type, projects, multipleUsers, projectsWithErrors]);

  const onTypeChange = useCallback<React.ChangeEventHandler<HTMLSelectElement>>(
    (e) => {
      formState.setValues({id: "no"});
      formState.changeHandler(e);
      setSelectedOptions([]);
      setCheckedOptions([]);
    },
    [formState],
  );

  const filterPassedTime = (time: Date) => {
    const moscowTime = getCurrentMoscowTime();
    if (time && moscowTime) {
      return time.getTime() > moscowTime;
    }
    return true;
  };

  const currentItems = useMemo<Array<PickedChallengeProject> | undefined>(() => {
    switch (type) {
      case "Challenge":
        return challenges?.filter((c) => !challengeIdsInsideProjects.has(c.id));
      case "Project": {
        return projects;
      }
      default:
        console.error(`Unsupported type of item to assign in AssignModal: "${type}"`);
        return undefined;
    }
  }, [type, challenges, projects, challengeIdsInsideProjects]);

  const currentItemsWithErrors = useMemo<
    Array<ProjectParticipationState | ChallengeParticipationState> | undefined
  >(() => {
    switch (type) {
      case "Challenge":
        if (multipleUsers) {
          return challenges?.filter((c) => !challengeIdsInsideProjects.has(c.id));
        }
        return challengesWithErrors?.filter((c) => !challengeIdsInsideProjects.has(c.id));
      case "Project": {
        let currentProjects;
        if (multipleUsers) {
          currentProjects = projects?.find((project) => project.id === formState.values.id)?.challenges || [];
        } else {
          currentProjects = projectsWithErrors?.find((project) =>
            project.id === formState.values.id)?.challenges || [];
        }

        return currentProjects;
      }
      default:
        console.error(`Unsupported type of item to assign in AssignModal: "${type}"`);
        return undefined;
    }
  }, [
    type,
    challengesWithErrors,
    projectsWithErrors,
    formState.values.id,
    projects,
    challenges,
    multipleUsers,
    challengeIdsInsideProjects,
  ]);

  const currentProjectsWithErrors = useMemo<Array<ProjectParticipationState> | undefined>(() => {
    if (type === "Project") {
      return projectsWithErrors;
    }
  }, [type, projectsWithErrors]);

  const selectedItem = useMemo<PickedChallengeProject | undefined>(
    () =>
      currentId === "no"
        ? undefined
        : (currentItems as Array<PickedChallengeProject> | undefined)?.find(
            (item: PickedChallengeProject) => item.id === currentId,
          ),
    [currentItems, currentId],
  );

  const onSubmit = useCallback(
    (usersIds: string[], emails?: string[], adminText?: string) => {
      const {id} = formState.values;
      if (!id && !multipleUsers) {
        return;
      }
      const challenges = selectedOptions
        .filter((option) => !option.alreadyAssigned)
        .map((option) => ({
          challengeId: option.value as string,
          activateAt: option.activateAt,
          deactivateAt: option.deactivateAt,
        }));
      params.onAssign && params.onAssign(challenges, usersIds, emails, adminText);
    },
    [formState.values, params, multipleUsers, selectedOptions],
  );

  const defaultSort = useMemo(
    () => ({
      field: AccountGroupSortField.NAME,
      direction: SortDirection.ASC,
    }),
    [],
  );

  const [sort, setSort] = useState<SortInput<AccountGroupSortField>>(defaultSort);

  const sortedGroups = useMemo(() => {
    if (!("groups" in params)) return [];
    const result = sortGroups(params.groups as Group[], sort);
    return result;
  }, [params, sort]);

  const challengeList = useMemo(() => {
    return (currentItemsWithErrors as ChallengeBase[])?.map((challenge) => ({
      value: challenge.id,
      label: challenge.name,
      alreadyAssigned: challenge.alreadyAssigned,
      activateAt: challenge.activateAt ? challenge.activateAt : undefined,
      deactivateAt: challenge.deactivateAt ? challenge.deactivateAt : undefined,
    }));
  }, [currentItemsWithErrors]);

  const [checkedOptions, setCheckedOptions] = useState<ChallengeOption[]>([]);

  React.useEffect(() => {
    if (!selectedOptions.length) {
      setActivateAt(undefined);
      setDeactivateAt(undefined);
    }
  }, [selectedOptions]);

  const onChangeActivateAt = (date: Date) => {
    setActivateAt?.(date || undefined);
    if (date && deactivateAt && deactivateAt <= date) setDeactivateAt(undefined);
    const updatedCheckedOptions = checkedOptions.map((option) => ({
      ...option,
      activateAt: dateToMoscowTimestamp(date),
    }));
    const updatedSelectedOptions = selectedOptions.map((option: ChallengeOption) => {
      const checkedOption = updatedCheckedOptions.find((o) => o.id === option.value);
      return checkedOption
        ? {
            value: checkedOption.id,
            label: checkedOption.name,
            alreadyAssigned: option.alreadyAssigned,
            activateAt: checkedOption.activateAt,
            deactivateAt: checkedOption.deactivateAt,
          }
        : option;
    });
    setCheckedOptions(updatedCheckedOptions);
    setSelectedOptions(updatedSelectedOptions);
  };

  const onChangeDeactivateAt = (date: Date) => {
    setDeactivateAt(date);
    if (date) {
      const updatedCheckedOptions = checkedOptions.map((option) => ({
        ...option,
        deactivateAt: dateToMoscowTimestamp(date),
      }));
      const updatedSelectedOptions = selectedOptions.map((option: ChallengeOption) => {
        const checkedOption = updatedCheckedOptions.find((o) => o.id === option.value);
        return checkedOption
          ? {
              value: checkedOption.id,
              label: checkedOption.name,
              alreadyAssigned: option.alreadyAssigned,
              activateAt: checkedOption.activateAt,
              deactivateAt: checkedOption.deactivateAt,
            }
          : option;
      });
      setCheckedOptions(updatedCheckedOptions);
      setSelectedOptions(updatedSelectedOptions);
    }
  };

  const resetActivateAt = () => {
    setActivateAt(undefined);
    const updatedCheckedOptions = checkedOptions.map((option) => ({
      ...option,
      activateAt: undefined,
    }));
    const updatedSelectedOptions = selectedOptions.map((option: ChallengeOption) => {
      const checkedOption = updatedCheckedOptions.find((o) => o.id === option.value);
      return checkedOption
        ? {
            value: checkedOption.id,
            label: checkedOption.name,
            alreadyAssigned: option.alreadyAssigned,
            activateAt: checkedOption.activateAt,
            deactivateAt: checkedOption.deactivateAt,
          }
        : option;
    });
    setCheckedOptions(updatedCheckedOptions);
    setSelectedOptions(updatedSelectedOptions);
  };

  const resetDeactivateAt = () => {
    setDeactivateAt(undefined);
    const updatedCheckedOptions = checkedOptions.map((option) => ({
      ...option,
      deactivateAt: undefined,
    }));
    const updatedSelectedOptions = selectedOptions.map((option: ChallengeOption) => {
      const checkedOption = updatedCheckedOptions.find((o) => o.id === option.value);
      return checkedOption
        ? {
            value: checkedOption.id,
            label: checkedOption.name,
            alreadyAssigned: option.alreadyAssigned,
            activateAt: checkedOption.activateAt,
            deactivateAt: checkedOption.deactivateAt,
          }
        : option;
    });
    setCheckedOptions(updatedCheckedOptions);
    setSelectedOptions(updatedSelectedOptions);
  };

  const allSelectableOptions = selectedOptions.filter((opt) => !opt.alreadyAssigned);

  const isAllChecked =
    allSelectableOptions.length > 0 &&
    allSelectableOptions.every((opt) => checkedOptions.some((checked) => checked.id === opt.value));

  const handleToggleAll = () => {
    if (isAllChecked) {
      const newCheckedData = checkedOptions.filter(
        (item) => selectedOptions.find((opt) => opt.value === item.id)?.alreadyAssigned,
      );
      setCheckedOptions(newCheckedData);
      setDeactivateAt(undefined);
      setActivateAt(undefined);
    } else {
      const newCheckedData = [
        ...checkedOptions.filter((item) => selectedOptions.find((opt) => opt.value === item.id)?.alreadyAssigned),
        ...allSelectableOptions.map((opt) => ({
          id: opt.value,
          name: opt.label,
          activateAt: opt.activateAt,
          deactivateAt: opt.deactivateAt,
        })),
      ];
      setCheckedOptions(newCheckedData);
    }
  };

  const handleMultiSelectChange = (data: {
    value: MultiValue<ChallengeOption>;
    checkedOptions: ChallengeParticipants[];
  }) => {
    setSelectedOptions(Array.from(data.value));
    setCheckedOptions(data.checkedOptions);
  };

  const submitDisabledSingle = !selectedOptions.some((challenge) => !challenge.alreadyAssigned);
  const submitDisabledMultiple = !selectedOptions.length;

  return (
    <form className={classes.root} method="post" onSubmit={formState.submitHandler}>
      <p className={classes.helpText}>{t("components.AssignModal.type.helpText")}</p>
      <Select className={classes.select} name="type" value={formState.values.type} onChange={onTypeChange}>
        <option value="Challenge">{t("components.AssignModal.type.challenge")}</option>
        <option value="Project">{t("components.AssignModal.type.сourse")}</option>
      </Select>

      <p className={classes.helpText}>{type === "Project" && t("components.AssignModal.helpText.project")}</p>

      {type === "Project" && !multipleUsers && !fetching
        ? currentProjectsWithErrors && (
            <Select
              name="id"
              className={classes.select}
              value={formState.values.id}
              onChange={formState.changeHandler}
            >
              <option value="no">{t("components.AssignModal.no")}</option>
              {currentProjectsWithErrors &&
                currentProjectsWithErrors.map((item) => (
                  <option key={item.id} value={item.id} translate="no">
                    {item.name}&nbsp;&nbsp;&nbsp;
                    {item.alreadyAssigned ? `(${t("components.AssignModal.helpText.alreadyAssigned")})` : ""}
                    {type === "Project" && !(item as ProjectParticipationState).challenges.length
                      ? `(${t("components.AssignModal.helpText.emptyProject")})`
                      : ""}
                  </option>
                ))}
            </Select>
          )
        : type === "Project" && (
            <Select
              name="id"
              className={classes.select}
              value={formState.values.id}
              onChange={formState.changeHandler}
            >
              <option value="no">{t("components.AssignModal.no")}</option>
              {currentItems &&
                currentItems.map((item) => (
                  <option key={item.id} value={item.id} translate="no">
                    {item.name}
                  </option>
                ))}
            </Select>
          )}

      <div className={classes.multiInput}>
        <p className={classes.helpText}>{t("components.AssignModal.helpText.challenges")}</p>
        <label className={classes.tickAllcheckboxLabel}>
          <Checkbox checked={isAllChecked} onChange={handleToggleAll} data-group-id={"all"} />
          <span className={classes.checkboxText}>{t("components.AssignModal.inputSelector.tickAll")}</span>
        </label>
        <MultiSelectField
          options={challengeList || []}
          onChange={handleMultiSelectChange}
          defaultValue={selectedOptions}
          name="actions"
          checkedOptions={checkedOptions}
          setCheckedOptions={setCheckedOptions}
          setActivateAt={setActivateAt}
          setDeactivateAt={setDeactivateAt}
        />
      </div>

      <div className={cn(classes.dateEditor, checkedOptions.length === 0 ? classes.dateEditorDisabled : "")}>
        <div className={classes.helpItem}>
          {checkedOptions.length === 0 ? (
            <p className={cn(classes.helpText, classes.titleText)}>
              {t("components.AssignModal.helpText.dateSingle")}
            </p>
          ) : null}

          {checkedOptions.length !== 0 ? (
            <>
              <p className={cn(classes.helpText, classes.titleText)}>
                {t("components.AssignModal.helpText.titleDateEditor", {count: checkedOptions.length})}
              </p>
              <WithTooltip
                className={classes.helpCircleIcon}
                as="span"
                helpText={t("components.AssignModal.helpText.timeMsk")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </>
          ) : null}
        </div>
        <div className="flex items-center flex-wrap gap-4">
          <div className="flex items-center gap-4">
            {t("components.MembersTable.activeFrom")}

            <DatePicker
              className={classes.customDatePicker}
              onChange={(value: Date) => onChangeActivateAt(value)}
              selected={activateAt}
              minDate={nowDate}
              filterTime={filterPassedTime}
              placeholderText={t("components.AssignModal.datePicker.activeFrom")}
              timeCaption={t("components.AssignModal.datePicker.time")}
              showTimeSelect
              dateFormat="Pp"
              timeIntervals={15}
              shouldCloseOnSelect={true}
              disabled={checkedOptions.length === 0}
            />
            <Link
              type="button"
              color="danger"
              disabled={checkedOptions.length === 0}
              className={classes.resetButton}
              onClick={resetActivateAt}
            >
              {t("common.reset")}
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {t("components.MembersTable.activeTo").toLowerCase()}

            <DatePicker
              className={classes.customDatePicker}
              onChange={(value: Date) => onChangeDeactivateAt(value ?? undefined)}
              selected={deactivateAt}
              minDate={activateAt ?? nowDate}
              filterTime={(time) => filterPassedTime(time) && (!activateAt || activateAt < time)}
              placeholderText={t("components.AssignModal.datePicker.activeTo")}
              timeCaption={t("components.AssignModal.datePicker.time")}
              showTimeSelect
              dateFormat="Pp"
              timeIntervals={15}
              shouldCloseOnSelect={true}
              disabled={checkedOptions.length === 0}
            />
            <Link
              type="button"
              color="danger"
              disabled={checkedOptions.length === 0}
              className={classes.resetButton}
              onClick={resetDeactivateAt}
            >
              {t("common.reset")}
            </Link>
          </div>
        </div>
      </div>

      {!multipleUsers && (
        <AdminText value={adminText} onChange={setAdminText}/>
      )}

      <div className={classes.bottom}>
        {!multipleUsers && (
          <Button type="submit" color="success" disabledColor="secondary" disabled={submitDisabledSingle}>
            {t("components.AssignModal.submit")}
          </Button>
        )}
      </div>

      {multipleUsers && (
        <>
          <p className={classes.helpText}>{t("components.AssignModal.helpText.multiple")}</p>
          <UsersSelect
            createNewUsers
            users={params.users}
            groups={sortedGroups}
            currentItem={selectedItem}
            onSort={setSort}
            defaultSort={defaultSort}
            sort={sort}
            submitDisabled={submitDisabledMultiple}
            onSubmit={onSubmit}
            withExcludeGroups={true}
            challenges={selectedOptions}
          />
        </>
      )}
    </form>
  );
}

export const AssignModal = {
  Header: AssignModalHeader,
  Content: AssignModalContent,
  fragments: {
    root: gql`
      fragment AssignModal on ChallengeProject {
        __typename
        id
      }
    `,
    challenges: gql`
      fragment AssignModalChallenges on Challenge {
        id
        name
      }
    `,
    projects: gql`
      fragment AssignModalProjects on Project {
        id
        name
        challenges {
          id
          name
        }
      }
    `,
  },
};

export default AssignModal;
